import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useColorTheme } from '../../context/ColorThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppPublishModal = ({ isOpen, onClose, campaign, sessionToken }) => {
  const { colorTheme } = useColorTheme();
  const [mode, setMode] = useState('numbers'); // 'numbers' or 'group'
  const [recipients, setRecipients] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groups, setGroups] = useState([]);
  const [selectedAds, setSelectedAds] = useState([]); // Changed to array for multiple selection
  const [isPublishing, setIsPublishing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      checkConnection();
      // Select all ads by default
      if (campaign?.generated_ads?.length > 0) {
        setSelectedAds(campaign.generated_ads.map((_, index) => index));
      }
    }
  }, [isOpen, campaign]);

  const checkConnection = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      // Check for service unavailable
      if (response.data.status === 'service_unavailable') {
        setConnectionStatus('service_unavailable');
        return;
      }
      
      setConnectionStatus(response.data.connected ? 'connected' : 'disconnected');
      
      if (response.data.connected) {
        await fetchGroups();
      }
    } catch (error) {
      console.error('Status check error:', error);
      // Check if it's a network or service error
      if (error.code === 'ERR_NETWORK' || error.response?.status === 500) {
        setConnectionStatus('service_unavailable');
      } else {
        setConnectionStatus('error');
      }
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/groups`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Fetch groups error:', error);
    }
  };

  const toggleAdSelection = (index) => {
    setSelectedAds(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const selectAllAds = () => {
    if (selectedAds.length === campaign.generated_ads.length) {
      setSelectedAds([]);
    } else {
      setSelectedAds(campaign.generated_ads.map((_, index) => index));
    }
  };

  const handlePublish = async () => {
    setError('');
    setMessage('');
    
    if (mode === 'numbers' && !recipients.trim()) {
      setError('Please enter at least one phone number');
      return;
    }
    
    if (mode === 'group' && !selectedGroup) {
      setError('Please select a WhatsApp group');
      return;
    }
    
    if (selectedAds.length === 0) {
      setError('Please select at least one ad to publish');
      return;
    }

    setIsPublishing(true);
    
    try {
      const recipientsList = mode === 'numbers' 
        ? recipients.split(/[,\n]/).map(r => r.trim()).filter(r => r)
        : null;

      let successCount = 0;
      let failCount = 0;

      // Send each selected ad
      for (let i = 0; i < selectedAds.length; i++) {
        const adIndex = selectedAds[i];
        const ad = campaign.generated_ads[adIndex];
        
        setMessage(`📤 Sending ad ${i + 1} of ${selectedAds.length}...`);

        const payload = {
          mode,
          caption: `${ad.title || ''}\n\n${ad.content || ''}`,
          mediaUrl: ad.image_url || ad.video_url,
          mediaType: ad.image_url ? 'image' : ad.video_url ? 'video' : null
        };

        if (mode === 'numbers') {
          payload.recipients = recipientsList;
        } else {
          payload.groupId = selectedGroup;
        }

        try {
          const response = await axios.post(`${API}/whatsapp/publish`, payload, {
            headers: { Authorization: `Bearer ${sessionToken}` }
          });

          if (response.data.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          console.error(`Failed to send ad ${adIndex}:`, err);
          failCount++;
        }

        // Small delay between messages to avoid rate limits
        if (i < selectedAds.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (failCount === 0) {
        setMessage(`✅ Successfully published ${successCount} ad(s) to WhatsApp!`);
      } else if (successCount > 0) {
        setMessage(`⚠️ Published ${successCount} ad(s), ${failCount} failed`);
      } else {
        setError(`❌ Failed to publish all ads`);
      }

      if (successCount > 0) {
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Publish error:', error);
      setError(error.response?.data?.detail || 'Failed to publish to WhatsApp');
    } finally {
      setIsPublishing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
              📱 Publish to WhatsApp
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          {connectionStatus === 'service_unavailable' ? (
            <div className="bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 p-4 rounded-lg mb-4">
              <p className="font-semibold">⚠️ WhatsApp Service Unavailable</p>
              <p className="text-sm mt-1">The WhatsApp integration service is not available in this environment. Please try again later or contact support.</p>
            </div>
          ) : connectionStatus === 'disconnected' ? (
            <div className="bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 p-4 rounded-lg mb-4">
              ⚠️ WhatsApp is not connected. Please connect in Settings first.
            </div>
          ) : connectionStatus === 'error' ? (
            <div className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 p-4 rounded-lg mb-4">
              ❌ Failed to check WhatsApp connection. Please try again.
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Send To:
                </label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setMode('numbers')}
                    className={`flex-1 py-2 px-4 rounded-lg ${
                      mode === 'numbers'
                        ? colorTheme === 'purple' ? 'bg-purple-600 text-white' :
                          colorTheme === 'blue' ? 'bg-blue-600 text-white' :
                          'bg-green-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    📞 Phone Numbers
                  </button>
                  <button
                    onClick={() => setMode('group')}
                    className={`flex-1 py-2 px-4 rounded-lg ${
                      mode === 'group'
                        ? colorTheme === 'purple' ? 'bg-purple-600 text-white' :
                          colorTheme === 'blue' ? 'bg-blue-600 text-white' :
                          'bg-green-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    👥 WhatsApp Group
                  </button>
                </div>
              </div>

              {mode === 'numbers' ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone Numbers (one per line or comma-separated):
                  </label>
                  <textarea
                    value={recipients}
                    onChange={(e) => setRecipients(e.target.value)}
                    placeholder="+1234567890\n+0987654321\nor\n+1234567890, +0987654321"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    rows={4}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Use international format (e.g., +1234567890)
                  </p>
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Group:
                  </label>
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">-- Select a group --</option>
                    {groups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.participants} members)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select Ads to Publish:
                  </label>
                  <button
                    onClick={selectAllAds}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {selectedAds.length === campaign?.generated_ads?.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {campaign?.generated_ads?.map((ad, index) => (
                    <div
                      key={index}
                      onClick={() => toggleAdSelection(index)}
                      className={`p-3 border rounded-lg cursor-pointer ${
                        selectedAds.includes(index)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                          : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900 dark:text-white">
                            {ad.ad_type}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                            {ad.title || ad.content?.substring(0, 60) || 'No content'}...
                          </div>
                          {(ad.image_url || ad.video_url) && (
                            <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                              📎 Has media
                            </div>
                          )}
                        </div>
                        <div className="ml-3">
                          {selectedAds.includes(index) ? (
                            <div className={`w-5 h-5 rounded flex items-center justify-center ${
                              colorTheme === 'purple' ? 'bg-purple-500' :
                              colorTheme === 'blue' ? 'bg-blue-500' :
                              'bg-green-500'
                            }`}>
                              <span className="text-white text-xs">✓</span>
                            </div>
                          ) : (
                            <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {selectedAds.length} ad(s) selected
                </p>
              </div>

              {message && (
                <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-lg">
                  {message}
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-lg">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 py-2 px-4 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePublish}
                  disabled={isPublishing || connectionStatus !== 'connected'}
                  className={`flex-1 py-2 px-4 ${
                    colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' :
                    colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                    'bg-green-600 hover:bg-green-700'
                  } text-white rounded-lg disabled:bg-gray-400`}
                >
                  {isPublishing ? 'Publishing...' : '📱 Publish to WhatsApp'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppPublishModal;
